/**
 * Which tool this page is (Nino, 27 Oct: "split first, this week"; Design's sketch "the split",
 * approved): the Command Centre runs drill days; the Class Creator makes and looks after classes,
 * drills and programmes. One codebase, one build: the address decides.
 *
 *   - classcreator.playbook.plus, classcreator.preview.playbook.plus → the Class Creator;
 *   - any other address → the Command Centre.
 *
 * Until Platform's Class Creator addresses are up (asked 27 Oct), the Class Creator also opens at
 * the Command Centre's own address with ?tool=class-creator, which this tab then remembers. Same
 * address, same sign-in, so nothing is asked twice. "Class Creator" is always written in full.
 */
export type Tool = 'command-centre' | 'class-creator';

export const TOOL_NAME: Record<Tool, string> = { 'command-centre': 'Command Centre', 'class-creator': 'Class Creator' };

/** The one line under each tool's name on its sign-in screen (Design, 27 Oct). */
export const TOOL_PURPOSE: Record<Tool, string> = {
  'command-centre': 'Run the drill days: sessions, people, health.',
  'class-creator': 'Make and look after classes, drills and programmes.',
};

/** Each tool's tile, also its favicon (public/). */
export const TOOL_ICON: Record<Tool, string> = { 'command-centre': '/favicon-cc.svg', 'class-creator': '/favicon-class-creator.svg' };

/**
 * Platform's addresses for the Class Creator. False until Platform says they're up; then the switch
 * and the forwarding use the other address instead of ?tool= on this one.
 */
export const CLASS_CREATOR_ADDRESSES_READY = false;

const OVERRIDE_KEY = 'playbook-tool';

/** The tool for an address (and this tab's ?tool= choice while the addresses aren't up). */
export function toolFor(hostname: string, search = '', remembered: string | null = null): Tool {
  if (/^classcreator\./i.test(hostname)) return 'class-creator';
  const asked = new URLSearchParams(search).get('tool');
  if (asked === 'class-creator' || asked === 'command-centre') return asked;
  return remembered === 'class-creator' ? 'class-creator' : 'command-centre';
}

function readTool(): Tool {
  if (typeof window === 'undefined') return 'command-centre';
  let remembered: string | null = null;
  try { remembered = window.sessionStorage.getItem(OVERRIDE_KEY); } catch { /* private mode: this page only */ }
  const tool = toolFor(window.location.hostname, window.location.search, remembered);
  if (!/^classcreator\./i.test(window.location.hostname)) {
    try { window.sessionStorage.setItem(OVERRIDE_KEY, tool); } catch { /* fine */ }
  }
  return tool;
}

/** This page's tool, fixed for the life of the page. */
export const TOOL: Tool = readTool();

/** Where the other tool opens, for a path in it ("Class Creator ↗", and old links that forward). */
export function otherToolUrl(target: Tool, path = '/', hostname = typeof window !== 'undefined' ? window.location.hostname : ''): string {
  const clean = path.startsWith('/') ? path : `/${path}`;
  if (CLASS_CREATOR_ADDRESSES_READY) {
    const preview = hostname.endsWith('.preview.playbook.plus');
    const live = !preview && hostname.endsWith('playbook.plus');
    if (preview || live) {
      const host = target === 'class-creator'
        ? (preview ? 'classcreator.preview.playbook.plus' : 'classcreator.playbook.plus')
        : (preview ? 'command-centre.preview.playbook.plus' : 'command-centre.playbook.plus');
      return `https://${host}${clean}`;
    }
  }
  const [p, q = ''] = clean.split('?');
  const params = new URLSearchParams(q);
  params.set('tool', target);
  return `${p}?${params.toString()}`;
}
